// O texto que a IA devolve, desenhado — sem biblioteca e sem `dangerouslySetInnerHTML`.
//
// Por que não uma biblioteca de markdown: a única coisa que falta é negrito, título, lista e
// código, e trazer um parser de markdown com plugin de sanitização para isso é mais
// superfície do que o problema pede. E por que não `dangerouslySetInnerHTML` de jeito
// nenhum: **este texto é gerado por um modelo alimentado com material de terceiros**. O PDF
// da aula é escrito por outra pessoa; se algum dia um deles contiver HTML, ele chega aqui
// pelo contexto e viraria markup executável na tela do aluno. Aqui tudo sai como nó de
// texto do React, que escapa por construção — o pior caso é markdown feio, nunca script.
//
// O que é reconhecido, e nada além: `### título`, `- item`, `1. item`, `**negrito**`,
// `*itálico*`, `` `código` `` e parágrafo. Tabela de markdown não é reconhecida e cai como
// texto — a IA é instruída a não abusar de tabela, e uma tabela mal desenhada é melhor que
// um parser errado.
import type { ReactNode } from "react";

/** Negrito, itálico e código dentro de uma linha. Ordem importa: código primeiro, para
 *  `**` dentro de código não virar negrito. */
function comMarcas(texto: string, chave: string): ReactNode[] {
  const saida: ReactNode[] = [];
  // um regex só, com alternativas, para não fatiar a string três vezes e perder a ordem
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)/g;
  let ultimo = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(texto)) !== null) {
    if (m.index > ultimo) saida.push(texto.slice(ultimo, m.index));
    const bruto = m[0];
    const k = `${chave}-${i++}`;
    if (bruto.startsWith("`")) {
      saida.push(
        <code key={k} className="rounded bg-superficie-2 px-1 py-0.5 font-mono text-[0.92em]">
          {bruto.slice(1, -1)}
        </code>,
      );
    } else if (bruto.startsWith("**")) {
      saida.push(<strong key={k} className="font-semibold text-titulo">{bruto.slice(2, -2)}</strong>);
    } else {
      saida.push(<em key={k}>{bruto.slice(1, -1)}</em>);
    }
    ultimo = m.index + bruto.length;
  }
  if (ultimo < texto.length) saida.push(texto.slice(ultimo));
  return saida;
}

type Bloco =
  | { tipo: "titulo"; nivel: number; texto: string }
  | { tipo: "paragrafo"; texto: string }
  | { tipo: "lista"; ordenada: boolean; itens: string[] }
  | { tipo: "regua" };

function emBlocos(md: string): Bloco[] {
  const blocos: Bloco[] = [];
  const linhas = md.replace(/\r\n?/g, "\n").split("\n");
  let paragrafo: string[] = [];
  let lista: { ordenada: boolean; itens: string[] } | null = null;

  const fecharParagrafo = () => {
    if (paragrafo.length) {
      blocos.push({ tipo: "paragrafo", texto: paragrafo.join(" ").trim() });
      paragrafo = [];
    }
  };
  const fecharLista = () => {
    if (lista) {
      blocos.push({ tipo: "lista", ...lista });
      lista = null;
    }
  };

  for (const linha of linhas) {
    const l = linha.trimEnd();
    if (!l.trim()) { fecharParagrafo(); fecharLista(); continue; }

    const t = /^(#{1,4})\s+(.*)$/.exec(l);
    if (t) { fecharParagrafo(); fecharLista(); blocos.push({ tipo: "titulo", nivel: t[1].length, texto: t[2] }); continue; }

    // A RÉGUA (`---`), que entrou em 02/09/2026 por uma medição: o prompt do
    // Leitor de Processos a EMITE — o formato obrigatório das respostas dele
    // termina com uma linha e a sugestão de próximas perguntas. Sem este caso a
    // régua caía em PARÁGRAFO, e o aluno lia "---" como texto no meio da
    // resposta (visto na captura da demonstração).
    //
    // Vem antes do marcador de lista de propósito: `---` não casa com
    // `[-*•]\s+` (não há espaço depois do primeiro traço), mas a ordem deixa a
    // intenção explícita para quem mexer aqui depois. E o `{3,}` exige três, para
    // um "- item" nunca virar régua.
    if (/^\s*(-{3,}|_{3,}|\*{3,})\s*$/.test(l)) {
      fecharParagrafo();
      fecharLista();
      blocos.push({ tipo: "regua" });
      continue;
    }

    const marcador = /^\s*[-*•]\s+(.*)$/.exec(l);
    const numero = /^\s*\d+[.)]\s+(.*)$/.exec(l);
    if (marcador || numero) {
      fecharParagrafo();
      const ordenada = !!numero;
      const item = (marcador ?? numero)![1];
      if (!lista || lista.ordenada !== ordenada) { fecharLista(); lista = { ordenada, itens: [] }; }
      lista.itens.push(item);
      continue;
    }

    fecharLista();
    paragrafo.push(l.trim());
  }
  fecharParagrafo();
  fecharLista();
  return blocos;
}

export function TextoDaIA({ texto }: { texto: string }) {
  const blocos = emBlocos(texto);
  return (
    <div className="space-y-2.5 text-[15px] leading-relaxed text-tinta">
      {blocos.map((b, i) => {
        if (b.tipo === "titulo") {
          return (
            <p key={i} className={b.nivel <= 2 ? "pt-1 text-base font-semibold text-titulo" : "pt-0.5 font-semibold text-titulo"}>
              {comMarcas(b.texto, `t${i}`)}
            </p>
          );
        }
        if (b.tipo === "regua") {
          // `aria-hidden`: a régua é separação visual, e um leitor de tela
          // anunciando "separador" no meio de cada resposta é ruído.
          return <hr key={i} aria-hidden="true" className="my-3 border-borda" />;
        }
        if (b.tipo === "lista") {
          const Lista = b.ordenada ? "ol" : "ul";
          return (
            <Lista
              key={i}
              className={`ml-4 space-y-1 ${b.ordenada ? "list-decimal" : "list-disc"}`}
            >
              {b.itens.map((item, j) => <li key={j}>{comMarcas(item, `l${i}-${j}`)}</li>)}
            </Lista>
          );
        }
        return <p key={i}>{comMarcas(b.texto, `p${i}`)}</p>;
      })}
    </div>
  );
}
